import { Avatar } from './bits.tsx';
import { identityColour } from '../lib/colour.ts';
import { hours } from '../lib/dates.ts';
import { werfLabel, type ScheduleItem } from '../api/planning.ts';
import type { DragPayload } from '../lib/drag.ts';

/**
 * One scheduled workpackage on one day.
 *
 * It carries `planRevision` into the drag payload so a drop can send back exactly the revision the
 * user was looking at. That is what turns a lost race into a clean 409 instead of a silent overwrite.
 *
 * ── WHAT IS READ FIRST ──────────────────────────────────────────────────────────
 * The werf name, then the task, then the hours. "BS-644" is the label nobody recognises and the only
 * one guaranteed unique, so it is present but small. A planner scanning a week is looking for a place,
 * not for a primary key.
 *
 * ── FOUR GESTURES ON ONE ELEMENT, AND HOW THEY STAY APART ───────────────────────
 *   click        — select (with ⇧ and ⌘ for many)
 *   double-click — open the panel
 *   drag         — move; the click handler fires only when the pointer did NOT travel, or every drop
 *                  would also select the thing it just dropped
 *   drag an edge — resize. Its own 10px target that calls `stopPropagation`, because a pointerdown
 *                  that arms both gestures makes a pull on the right edge slide the whole job sideways.
 *   right-click  — the menu, on the selection rather than on this chip alone
 *
 * ── WHY A BAR KNOWS WHERE IT STARTS AND ENDS ────────────────────────────────────
 * The schedule returns one row per DAY, so a three-day job is three chips. Rounding all three the same
 * way makes them read as three separate one-day jobs. `spanStart`/`spanEnd` square off the inner edges
 * so the run reads as one continuous bar — and the resize grips appear only on the real ends, which is
 * also the only place dragging them means anything.
 */
export function Chip({
  item, dragProps, resizeProps, showResource, onOpen, onSelect, onContext, compact, selected,
  onPressStart, onPressEnd, onPressMove, spanStart = true, spanEnd = true,
}: {
  item: ScheduleItem;
  dragProps?: (p: DragPayload) => Record<string, unknown>;
  resizeProps?: (p: DragPayload, edge: 'start' | 'end') => Record<string, unknown>;
  showResource?: boolean;
  onOpen?: (item: ScheduleItem) => void;
  onSelect?: (item: ScheduleItem, e: React.MouseEvent) => void;
  onContext?: (item: ScheduleItem, e: React.MouseEvent) => void;
  /** Long-press on touch: the phone's right-click. See Calendar for the timing and why. */
  onPressStart?: (item: ScheduleItem, e: React.PointerEvent) => void;
  onPressEnd?: () => void;
  onPressMove?: (e: React.PointerEvent) => void;
  compact?: boolean;
  selected?: boolean;
  spanStart?: boolean;
  spanEnd?: boolean;
}) {
  const c = identityColour(String(item.resourceId), item.isTeam);
  const werf = werfLabel(item);
  const payload: DragPayload = {
    workpackageId: item.workpackageId, projectId: item.projectId,
    assignmentId: item.assignmentId, resourceId: item.resourceId,
    title: item.title, planRevision: item.planRevision,
    plannedStart: item.plannedStart, plannedEnd: item.plannedEnd,
    day: item.day, allocationId: item.allocationId ?? null,
    minutes: item.minutes, allocatedMinutes: item.allocatedMinutes,
  };
  const p = dragProps ? dragProps(payload) : {};
  const cls = [
    'chip',
    compact ? 'compact' : '',
    selected ? 'selected' : '',
    spanStart ? '' : 'cont-left',
    spanEnd ? '' : 'cont-right',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} style={{ background: c.bg, borderColor: selected ? undefined : c.border }} {...p}
         onClick={(e) => { e.stopPropagation(); onSelect?.(item, e); }}
         onDoubleClick={onOpen ? (e) => { e.stopPropagation(); onOpen(item); } : undefined}
         onContextMenu={onContext ? (e) => onContext(item, e) : undefined}
         onPointerDown={(e) => { (p.onPointerDown as ((e: React.PointerEvent) => void) | undefined)?.(e); onPressStart?.(item, e); }}
         onPointerUp={onPressEnd}
         onPointerMove={onPressMove}
         onPointerCancel={onPressEnd}
         aria-selected={selected}
         title={onOpen ? 'Klik om te selecteren, dubbelklik om te wijzigen, sleep om te verplaatsen' : undefined}
         aria-label={`${werf.name}${werf.code ? ` (${werf.code})` : ''}, ${item.title}, ${hours(item.minutes)}${showResource && item.resourceName ? `, ${item.resourceName}` : ''}`}>
      {/* THE GRIPS SIT ON THE REAL ENDS ONLY. A middle day has no start to drag: offering a handle
          there would move an edge the planner cannot see, two columns away. */}
      {resizeProps && spanStart && item.plannedStart && <span {...resizeProps(payload, 'start')} />}
      {resizeProps && spanEnd && item.plannedEnd && <span {...resizeProps(payload, 'end')} />}
      <span className="t">{werf.name}</span>
      <span className="s">
        <span className="task">{item.title}</span>
        <span>·</span>
        <span>{hours(item.minutes)}</span>
      </span>
      {werf.code && !compact && <span className="code">{werf.code}</span>}
      {showResource && item.resourceName && (
        <span className="s" style={{ alignItems: 'center' }}>
          <Avatar id={String(item.resourceId)} name={item.resourceName} isTeam={item.isTeam} />
          <span>{item.resourceName}</span>
        </span>
      )}
    </div>
  );
}
